import React, { useCallback, useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SendIcon from "@mui/icons-material/Send";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import Logger from "../../services/Logger";
import ConfirmDialog from "../components/ConfirmDialog";
import Item from "../components/Item";
import WeeklyEmailPanel from "./WeeklyEmailPanel";
import WeeklyEmailService from "./WeeklyEmailService";
import WeeklyIssuePage from "./WeeklyIssuePage";
import { DEFAULT_WEEKLY_SUBJECT, defaultWeeklyHtml } from "./weeklyEmailDefaults";

/** Testing only — sample send always goes to this address (not the full mailing list). */
const SAMPLE_EMAIL = "syc0701@gmail.com";
const SEND_SAMPLE_TOOLTIP =
    `Testing only: sends this weekly email to ${SAMPLE_EMAIL}. Does not mail the full list.`;

function statusLabel(status) {
    if (status === "DRAFT") return "Working";
    if (status === "CONFIRMED") return "Confirmed";
    if (status === "SENT") return "Sent";
    return status || "—";
}

function statusColor(status) {
    if (status === "DRAFT") return "warning";
    if (status === "CONFIRMED") return "info";
    if (status === "SENT") return "success";
    return "default";
}

function formatWhen(value) {
    if (value == null || value === "") return "—";
    const n = Number(value);
    if (!Number.isFinite(n)) return "—";
    return dayjs(n).format("YYYY-MM-DD HH:mm");
}

export default function WeeklyIssuesPanel({ openTemplate = false }) {
    const [showTemplate, setShowTemplate] = useState(openTemplate);
    const [openIssueId, setOpenIssueId] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [removingId, setRemovingId] = useState("");
    const [sendingId, setSendingId] = useState("");
    const [rowToRemove, setRowToRemove] = useState(null);
    const [rowToSend, setRowToSend] = useState(null);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const list = await WeeklyEmailService.listIssues();
            setRows(list);
        } catch (err) {
            Logger.error("List weekly emails:", err);
            setError(typeof err === "string" ? err : err?.message || "Failed to load weekly emails");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (openTemplate) {
            setShowTemplate(true);
        }
    }, [openTemplate]);

    const handleCreate = async () => {
        if (creating) return;
        setCreating(true);
        setError("");
        try {
            await WeeklyEmailService.save({
                locale: "en",
                subject: DEFAULT_WEEKLY_SUBJECT,
                htmlContent: defaultWeeklyHtml(),
            });
            const issue = await WeeklyEmailService.createIssue();
            if (!issue?.id) return;
            const opened = issue.status === "DRAFT"
                ? await WeeklyEmailService.refreshIssue(issue.id)
                : issue;
            setShowTemplate(false);
            setOpenIssueId(String(opened?.id || issue.id));
        } catch (err) {
            Logger.error("Create weekly email:", err);
            setError(typeof err === "string" ? err : err?.message || "Failed to create weekly email");
        } finally {
            setCreating(false);
        }
    };

    const handleRemove = async (id) => {
        const nextId = id || rowToRemove?.id;
        if (!nextId || removingId) return;
        setRemovingId(String(nextId));
        setError("");
        try {
            await WeeklyEmailService.removeIssue(nextId);
            setRowToRemove(null);
            if (String(openIssueId) === String(nextId)) {
                setOpenIssueId("");
            }
            await load();
        } catch (err) {
            Logger.error("Remove weekly email:", err);
            setError(typeof err === "string" ? err : err?.message || "Failed to remove weekly email");
        } finally {
            setRemovingId("");
        }
    };

    const handleSendSample = async () => {
        const nextId = rowToSend?.id;
        if (!nextId || sendingId) return;
        setSendingId(String(nextId));
        setError("");
        setNotice("");
        try {
            await WeeklyEmailService.sendSample(nextId);
            setRowToSend(null);
            setNotice(`Sent to ${SAMPLE_EMAIL}`);
        } catch (err) {
            Logger.error("Send weekly email sample:", err);
            setError(typeof err === "string" ? err : err?.message || "Failed to send weekly email");
        } finally {
            setSendingId("");
        }
    };

    const columns = [
        {
            field: "status",
            headerName: "Status",
            width: 130,
            renderCell: (params) => (
                <Chip size="small" label={statusLabel(params.value)} color={statusColor(params.value)} />
            ),
        },
        {
            field: "subject",
            headerName: "Subject",
            flex: 1.4,
            minWidth: 220,
        },
        {
            field: "scheduledFor",
            headerName: "Due date",
            width: 140,
            valueFormatter: (value) => value || "—",
        },
        {
            field: "sentAt",
            headerName: "Sent",
            width: 160,
            valueFormatter: (value) => formatWhen(value),
        },
        {
            field: "sentCount",
            headerName: "Result",
            width: 140,
            valueGetter: (value, row) => {
                if (row?.status !== "SENT") return "—";
                const sent = Number(row.sentCount) || 0;
                const failed = Number(row.failedCount) || 0;
                return failed > 0 ? `${sent} sent, ${failed} failed` : `${sent} sent`;
            },
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 130,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const row = params.row;
                const removing = removingId === String(row.id);
                const sending = sendingId === String(row.id);
                return (
                    <Box
                        sx={{ display: "flex", alignItems: "center", height: "100%" }}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <Tooltip title={SEND_SAMPLE_TOOLTIP}>
                            <span>
                                <IconButton
                                    aria-label={SEND_SAMPLE_TOOLTIP}
                                    color="primary"
                                    disabled={Boolean(removingId) || Boolean(sendingId) || creating}
                                    onClick={() => setRowToSend(row)}
                                    sx={{ minWidth: 44, minHeight: 44 }}
                                >
                                    {sending
                                        ? <CircularProgress size={16} color="inherit" />
                                        : <SendIcon fontSize="small" />}
                                </IconButton>
                            </span>
                        </Tooltip>
                        <IconButton
                            aria-label="Remove weekly email"
                            color="error"
                            disabled={Boolean(removingId) || Boolean(sendingId) || creating}
                            onClick={() => setRowToRemove(row)}
                            sx={{ minWidth: 44, minHeight: 44 }}
                        >
                            {removing ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon fontSize="small" />}
                        </IconButton>
                    </Box>
                );
            },
        },
    ];

    if (openIssueId) {
        return (
            <WeeklyIssuePage
                issueId={openIssueId}
                onBack={() => {
                    setOpenIssueId("");
                    load();
                }}
            />
        );
    }

    if (showTemplate) {
        return (
            <Item>
                <Button
                    variant="text"
                    onClick={() => setShowTemplate(false)}
                    sx={{ textTransform: "none", mb: 1, minHeight: 44 }}
                >
                    Weekly email
                </Button>
                <WeeklyEmailPanel />
            </Item>
        );
    }

    return (
        <Item>
            <Card>
                <CardContent>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                            mb: 1,
                        }}
                    >
                        <Typography variant="h6" component="h2">
                            Weekly email
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                                variant="text"
                                onClick={() => setShowTemplate(true)}
                                sx={{ textTransform: "none" }}
                            >
                                Template
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                disabled={creating}
                                onClick={handleCreate}
                                sx={{ textTransform: "none" }}
                            >
                                Create
                            </Button>
                        </Box>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Create turns the template page into this week’s email so you can check it.
                        After you confirm, the Monday scheduler sends that email. It is not sent before then.
                    </Typography>
                    {notice ? (
                        <Typography variant="body2" color="success.main" sx={{ mb: 2 }}>
                            {notice}
                        </Typography>
                    ) : null}
                    {error ? (
                        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                            {error}
                        </Typography>
                    ) : null}
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        getRowId={(row) => row.id}
                        loading={loading || creating}
                        autoHeight
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10 } },
                        }}
                        disableRowSelectionOnClick
                        onRowClick={(params) => {
                            if (params.row?.id) {
                                setOpenIssueId(String(params.row.id));
                            }
                        }}
                        localeText={{
                            noRowsLabel: loading ? "Loading…" : "No weekly emails yet.",
                        }}
                        sx={{ minHeight: 280, "& .MuiDataGrid-row": { cursor: "pointer" } }}
                    />
                    <ConfirmDialog
                        open={Boolean(rowToRemove)}
                        setOpen={(open) => {
                            if (!open) setRowToRemove(null);
                        }}
                        title="Remove weekly email"
                        targetName={rowToRemove?.subject || ""}
                        targetId={rowToRemove?.id}
                        message="Remove this weekly email? The template stays as it is."
                        okText="Remove"
                        cancelText="Cancel"
                        callback={handleRemove}
                    />
                    <Dialog
                        open={Boolean(rowToSend)}
                        onClose={() => {
                            if (!sendingId) setRowToSend(null);
                        }}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>Send test weekly email</DialogTitle>
                        <DialogContent>
                            <Typography variant="body1">
                                Testing only. Sends this issue to your inbox — not the full mailing list:
                            </Typography>
                            <Typography variant="body1" sx={{ mt: 1, fontWeight: 700 }}>
                                {SAMPLE_EMAIL}
                            </Typography>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button
                                onClick={() => setRowToSend(null)}
                                disabled={Boolean(sendingId)}
                                sx={{ textTransform: "none", minHeight: 44 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSendSample}
                                disabled={Boolean(sendingId)}
                                sx={{ textTransform: "none", minHeight: 44 }}
                            >
                                {sendingId ? "Sending…" : "Send"}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </CardContent>
            </Card>
        </Item>
    );
}
